import { c as _c } from "react/compiler-runtime";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { WorldMap } from "@components/world-map/world-map";
import { CollapseIcon, ExpandIcon, XMarkCircleFillIcon } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui-wrapper";
import { ProjectMarkerPopupContent } from "@pages/projects/projects-map/project-marker-popup-content";
import { ProjectsMapContainer } from "@pages/projects/projects-map/projects-map-container";
import { ProjectsMapEmptyOverlay } from "@pages/projects/projects-map/projects-map-empty-overlay";
import { ProjectsMapInfoPage } from "@pages/projects/projects-map/projects-map-info-page";
import { ProjectsMapList } from "@pages/projects/projects-map/projects-map-list";
import { ProjectsMapSkeleton } from "@pages/projects/projects-map/projects-map-skeleton";
import { useProjectsMapDeepLink } from "@pages/projects/projects-map/use-projects-map-deep-link";
import { useProjectsMapLoadTracking } from "@pages/projects/projects-map/use-projects-map-load-tracking";
import { WORLD_MAP_CONTROL_ICON_SIZE } from "@components/world-map/world-map-constants";
import { mapProjectsByArchivingStateSelector, mapProjectsFetchingStatusSelector } from "@store/projects-map/projects-map-selectors";
import { fetchMapProjects } from "@store/projects-map/projects-map-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector, selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { searchSelector } from "@store/ui/ui-selector";
import { getSvgColoredIconCss } from "@styles/common-styles";
import { MAP_EXPORT_EXCLUDED_COLUMNS } from "@utils/file-data-export";
import { sentryCaptureError } from "@utils/sentry-utils";
import { sortProjects } from "@utils/project-utils";
import { matchesAnySearchWord } from "@utils/string-utils";
import { WorldMapEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { ProjectsSort } from "@components/projects/projects-sort";
import { useProjectsFilters } from "@components/projects/projects-filters/use-projects-filters";
import { isSdbProjectWithPosition } from "@custom-types/project-type-guards";
import { PALETTE_VARS } from "@styles/palette-vars";
const EMPTY_SET = new Set();
/** World map that displays projects */
export function ProjectsMap(t0) {
  const $ = _c(170);
  const {
    companyId,
    projectArchivingState,
    sortBy,
    setSortBy
  } = t0;
  const dispatch = useAppDispatch();
  const {
    isScreenLgOrLarger
  } = useMediaQueryList();
  const mapRef = useRef(null);
  const {
    initialView,
    onViewStable,
    resetView
  } = useProjectsMapDeepLink();
  const {
    handleMapReady,
    handleProjectsFetchStart,
    handleProjectsFetchEnd
  } = useProjectsMapLoadTracking();
  const {
    trackEvent
  } = useTrackEvent();
  const [viewportProjectIds, setViewportProjectIds] = useState(null);
  const [highlightedIds, setHighlightedIds] = useState(EMPTY_SET);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  let t1;
  if ($[0] !== isMapFullscreen || $[1] !== trackEvent) {
    t1 = () => {
      trackEvent({
        name: WorldMapEvents.useMapControl,
        props: {
          control: "fullscreen",
          controlValue: isMapFullscreen ? "exitFullscreen" : "enterFullscreen",
          mapContext: "projects map"
        }
      });
      setIsMapFullscreen(_temp);
    };
    $[0] = isMapFullscreen;
    $[1] = trackEvent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const toggleMapFullscreen = t1;
  const fetchingStatus = useAppSelector(mapProjectsFetchingStatusSelector);
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = selectedEntitiesSelector("mapProjects");
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectedMapProjects = useAppSelector(t2);
  const isAnyRowSelected = selectedMapProjects.length !== 0;
  let t3;
  if ($[4] !== projectArchivingState) {
    t3 = mapProjectsByArchivingStateSelector(projectArchivingState);
    $[4] = projectArchivingState;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const mapProjectsSelector = t3;
  const allProjects = useAppSelector(mapProjectsSelector);
  const {
    debouncedSearchText
  } = useAppSelector(searchSelector);
  let t4;
  if ($[6] !== debouncedSearchText) {
    t4 = debouncedSearchText.trim() || undefined;
    $[6] = debouncedSearchText;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const search = t4;
  const hasSearchQuery = search !== undefined;
  let t5;
  if ($[8] !== projectArchivingState) {
    t5 = {
      projectArchivingState
    };
    $[8] = projectArchivingState;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    isAnyFilterApplied,
    filterProjects,
    filterChips
  } = useProjectsFilters(t5);
  const isSearchingOrFiltering = hasSearchQuery || isAnyFilterApplied;
  const isLoading = fetchingStatus === "uninitialized" || fetchingStatus === "pending";
  let t6;
  if ($[10] !== allProjects || $[11] !== search) {
    t6 = search ? allProjects.filter(p => matchesAnySearchWord(p.name, search)) : allProjects;
    $[10] = allProjects;
    $[11] = search;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const searchedProjects = t6;
  let t7;
  if ($[13] !== filterProjects || $[14] !== searchedProjects) {
    t7 = filterProjects(searchedProjects);
    $[13] = filterProjects;
    $[14] = searchedProjects;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const filteredProjects = t7;
  let t8;
  if ($[16] !== filteredProjects) {
    t8 = filteredProjects.filter(isSdbProjectWithPosition);
    $[16] = filteredProjects;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const filteredProjectsWithPosition = t8;
  let t9;
  if ($[18] !== filteredProjectsWithPosition || $[19] !== viewportProjectIds) {
    t9 = viewportProjectIds ? filteredProjectsWithPosition.filter(p_0 => viewportProjectIds.has(p_0.id)) : filteredProjectsWithPosition;
    $[18] = filteredProjectsWithPosition;
    $[19] = viewportProjectIds;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const inViewport = t9;
  let t10;
  if ($[21] !== inViewport || $[22] !== sortBy) {
    t10 = sortProjects(inViewport, sortBy);
    $[21] = inViewport;
    $[22] = sortBy;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const visibleProjects = t10;
  let t11;
  if ($[24] !== visibleProjects) {
    t11 = new Set(visibleProjects.map(_temp2));
    $[24] = visibleProjects;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const visibleIds = t11;
  let t12;
  if ($[26] !== filteredProjects || $[27] !== sortBy || $[28] !== visibleIds) {
    let t13;
    if ($[30] !== visibleIds) {
      t13 = p_2 => !visibleIds.has(p_2.id);
      $[30] = visibleIds;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    const remaining = filteredProjects.filter(t13);
    t12 = sortProjects(remaining, sortBy);
    $[26] = filteredProjects;
    $[27] = sortBy;
    $[28] = visibleIds;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const otherProjects = t12;
  let t13;
  if ($[32] !== otherProjects || $[33] !== visibleProjects) {
    t13 = [...visibleProjects, ...otherProjects];
    $[32] = otherProjects;
    $[33] = visibleProjects;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const projectsMapList = t13;
  const hasNoProjectsWithPosition = fetchingStatus === "succeeded" && filteredProjects.length > 0 && filteredProjectsWithPosition.length === 0;
  const [wasMapEmptyOnLoad, setWasMapEmptyOnLoad] = useState(null);
  let t14;
  let t15;
  if ($[35] !== allProjects || $[36] !== fetchingStatus || $[37] !== wasMapEmptyOnLoad) {
    t14 = () => {
      if (fetchingStatus !== "succeeded" || wasMapEmptyOnLoad !== null) {
        return;
      }
      setWasMapEmptyOnLoad(!allProjects.some(isSdbProjectWithPosition));
    };
    t15 = [fetchingStatus, allProjects, wasMapEmptyOnLoad];
    $[35] = allProjects;
    $[36] = fetchingStatus;
    $[37] = wasMapEmptyOnLoad;
    $[38] = t14;
    $[39] = t15;
  } else {
    t14 = $[38];
    t15 = $[39];
  }
  useEffect(t14, t15);
  const shouldShowEmptyCaption = wasMapEmptyOnLoad === true && hasNoProjectsWithPosition && !isSearchingOrFiltering;
  const [isMapNavigated, setIsMapNavigated] = useState(false);
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setIsMapNavigated(true);
    };
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const markMapNavigated = t16;
  let t17;
  if ($[41] !== onViewStable) {
    t17 = view => {
      markMapNavigated();
      onViewStable(view);
    };
    $[41] = onViewStable;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const handleViewStable = t17;
  let t18;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = entityId => {
      setHighlightedIds(entityId ? new Set([entityId]) : EMPTY_SET);
      mapRef.current?.highlightEntity(entityId);
    };
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const onMarkerHover = t18;
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = memberIds => {
      setHighlightedIds(memberIds.length > 0 ? new Set(memberIds) : EMPTY_SET);
    };
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  const onClusterHover = t19;
  let t20;
  if ($[45] !== projectsMapList) {
    t20 = new Set(projectsMapList.map(_temp3));
    $[45] = projectsMapList;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const projectsMapListIds = t20;
  let t21;
  if ($[47] !== isMapFullscreen) {
    t21 = isMapFullscreen ? <CollapseIcon sx={{
      width: WORLD_MAP_CONTROL_ICON_SIZE,
      height: WORLD_MAP_CONTROL_ICON_SIZE
    }} /> : <ExpandIcon sx={{
      width: WORLD_MAP_CONTROL_ICON_SIZE,
      height: WORLD_MAP_CONTROL_ICON_SIZE
    }} />;
    $[47] = isMapFullscreen;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const t22 = isMapFullscreen ? "Exit full screen" : "Full screen";
  let t23;
  if ($[49] !== t21 || $[50] !== t22 || $[51] !== toggleMapFullscreen) {
    t23 = [{
      icon: t21,
      tooltip: t22,
      onClick: toggleMapFullscreen
    }];
    $[49] = t21;
    $[50] = t22;
    $[51] = toggleMapFullscreen;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = entities => setViewportProjectIds(new Set(entities.map(_temp4)));
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== filteredProjectsWithPosition || $[55] !== handleMapReady || $[56] !== handleViewStable || $[57] !== initialView || $[58] !== resetView || $[59] !== t23) {
    t25 = <WorldMap entities={filteredProjectsWithPosition} mapContext="projects map" shouldShowMapControls={true} buttons={t23} onVisibleEntitiesChange={t24} mapRef={mapRef} initialView={initialView} onViewStable={handleViewStable} onResetView={resetView} onMapReady={handleMapReady} onEntityHover={onMarkerHover} onClusterHover={onClusterHover} renderPopupContent={_temp5} />;
    $[54] = filteredProjectsWithPosition;
    $[55] = handleMapReady;
    $[56] = handleViewStable;
    $[57] = initialView;
    $[58] = resetView;
    $[59] = t23;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  const worldMapElement = t25;
  let t26;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = (position, zoom) => {
      mapRef.current?.highlightEntity(null);
      mapRef.current?.goTo(position, zoom);
    };
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  const goTo = t26;
  let t27;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = entityId_0 => {
      mapRef.current?.highlightEntity(entityId_0);
    };
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  const onHoverChange = t27;
  let t28;
  if ($[63] !== companyId || $[64] !== hasNoProjectsWithPosition || $[65] !== highlightedIds || $[66] !== isSearchingOrFiltering || $[67] !== otherProjects || $[68] !== projectArchivingState || $[69] !== sortBy || $[70] !== visibleProjects) {
    t28 = <ProjectsMapList visibleProjects={visibleProjects} otherProjects={otherProjects} companyId={companyId} projectArchivingState={projectArchivingState} goTo={goTo} onHoverChange={onHoverChange} highlightedProjectIds={highlightedIds} isSearchingOrFiltering={isSearchingOrFiltering} hasNoProjectsWithPosition={hasNoProjectsWithPosition} sortBy={sortBy} />;
    $[63] = companyId;
    $[64] = hasNoProjectsWithPosition;
    $[65] = highlightedIds;
    $[66] = isSearchingOrFiltering;
    $[67] = otherProjects;
    $[68] = projectArchivingState;
    $[69] = sortBy;
    $[70] = visibleProjects;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  const projectsMapListElement = t28;
  let t29;
  if ($[72] !== companyId || $[73] !== dispatch || $[74] !== handleProjectsFetchEnd || $[75] !== handleProjectsFetchStart) {
    t29 = async () => {
      handleProjectsFetchStart();
      ;
      try {
        const projects = await dispatch(fetchMapProjects({
          companyId
        })).unwrap();
        handleProjectsFetchEnd({
          outcome: "succeeded",
          projectCount: projects.length,
          mappableProjectCount: projects.filter(isSdbProjectWithPosition).length
        });
      } catch (t30) {
        const error = t30;
        handleProjectsFetchEnd({
          outcome: "failed",
          projectCount: 0,
          mappableProjectCount: 0
        });
        sentryCaptureError({
          error,
          title: "Failed to load the map projects."
        });
      }
    };
    $[72] = companyId;
    $[73] = dispatch;
    $[74] = handleProjectsFetchEnd;
    $[75] = handleProjectsFetchStart;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  const getProjects = t29;
  let t30;
  let t31;
  if ($[77] !== fetchingStatus || $[78] !== getProjects) {
    t30 = () => {
      if (fetchingStatus === "uninitialized") {
        getProjects();
      }
    };
    t31 = [fetchingStatus, getProjects];
    $[77] = fetchingStatus;
    $[78] = getProjects;
    $[79] = t30;
    $[80] = t31;
  } else {
    t30 = $[79];
    t31 = $[80];
  }
  useEffect(t30, t31);
  let t32;
  let t33;
  if ($[81] !== dispatch || $[82] !== projectsMapListIds || $[83] !== selectedRowIds) {
    t32 = () => {
      if (selectedRowIds.length === 0) {
        return;
      }
      const nextSelectedRowIds = selectedRowIds.filter(rowId => projectsMapListIds.has(rowId));
      if (nextSelectedRowIds.length !== selectedRowIds.length) {
        dispatch(setInitialFetchingItems(nextSelectedRowIds));
      }
    };
    t33 = [dispatch, projectsMapListIds, selectedRowIds];
    $[81] = dispatch;
    $[82] = projectsMapListIds;
    $[83] = selectedRowIds;
    $[84] = t32;
    $[85] = t33;
  } else {
    t32 = $[84];
    t33 = $[85];
  }
  useEffect(t32, t33);
  const isToolbarActionsReady = fetchingStatus === "succeeded" && viewportProjectIds !== null && projectsMapList.length > 0;
  const isListReady = fetchingStatus === "succeeded" && viewportProjectIds !== null;
  const projectsForExport = isAnyRowSelected ? selectedMapProjects : projectsMapList;
  let t34;
  if ($[86] !== companyId || $[87] !== isToolbarActionsReady || $[88] !== projectArchivingState || $[89] !== projectsForExport) {
    t34 = isToolbarActionsReady ? <BulkExportProjects companyId={companyId} selectedProjects={projectsForExport} subjectType="workspace" projectStatus={projectArchivingState} areEntitiesSelected={true} buttonType="standard" columnsToExclude={MAP_EXPORT_EXCLUDED_COLUMNS} viewMode="map" /> : undefined;
    $[86] = companyId;
    $[87] = isToolbarActionsReady;
    $[88] = projectArchivingState;
    $[89] = projectsForExport;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  const exportComponent = t34;
  const t35 = isSearchingOrFiltering ? allProjects.length : undefined;
  let t36;
  if ($[91] !== isToolbarActionsReady || $[92] !== setSortBy || $[93] !== sortBy) {
    t36 = isToolbarActionsReady && <ProjectsSort key="projects-sort" sortBy={sortBy} setSortBy={setSortBy} />;
    $[91] = isToolbarActionsReady;
    $[92] = setSortBy;
    $[93] = sortBy;
    $[94] = t36;
  } else {
    t36 = $[94];
  }
  let t37;
  if ($[95] !== exportComponent || $[96] !== filterChips || $[97] !== filteredProjects.length || $[98] !== isAnyFilterApplied || $[99] !== isLoading || $[100] !== isScreenLgOrLarger || $[101] !== isToolbarActionsReady || $[102] !== t35 || $[103] !== t36) {
    t37 = <EntitiesToolbar toolbarEntityName="Project" numberOfRows={filteredProjects.length} totalNumberOfRows={t35} isLoading={isLoading} shouldShowRowsLabel={isToolbarActionsReady} shouldStack={isScreenLgOrLarger} shouldRemoveTopMargin={true} sortActions={t36} exportComponent={exportComponent} filterChips={filterChips} isAnyFilterApplied={isAnyFilterApplied} shouldSkipValidation={true} />;
    $[95] = exportComponent;
    $[96] = filterChips;
    $[97] = filteredProjects.length;
    $[98] = isAnyFilterApplied;
    $[99] = isLoading;
    $[100] = isScreenLgOrLarger;
    $[101] = isToolbarActionsReady;
    $[102] = t35;
    $[103] = t36;
    $[104] = t37;
  } else {
    t37 = $[104];
  }
  const toolbarElement = t37;
  let t38;
  if ($[105] !== fetchingStatus || $[106] !== isListReady || $[107] !== projectsMapListElement) {
    t38 = function renderListContent() {
      if (fetchingStatus === "rejected") {
        return <ProjectsMapInfoPage title="Failed to load the map projects" subtitle="Please reload the page and try again." source={_temp6} />;
      }
      if (!isListReady) {
        return <ProjectsMapSkeleton />;
      }
      return projectsMapListElement;
    };
    $[105] = fetchingStatus;
    $[106] = isListReady;
    $[107] = projectsMapListElement;
    $[108] = t38;
  } else {
    t38 = $[108];
  }
  const renderListContent = t38;
  let t39;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      width: "100%",
      height: "100%",
      position: "relative"
    };
    $[109] = t39;
  } else {
    t39 = $[109];
  }
  let t40;
  if ($[110] !== isMapNavigated || $[111] !== projectArchivingState || $[112] !== shouldShowEmptyCaption) {
    t40 = shouldShowEmptyCaption && <ProjectsMapEmptyOverlay projectArchivingState={projectArchivingState} isDismissed={isMapNavigated} />;
    $[110] = isMapNavigated;
    $[111] = projectArchivingState;
    $[112] = shouldShowEmptyCaption;
    $[113] = t40;
  } else {
    t40 = $[113];
  }
  let t41;
  if ($[114] !== t40 || $[115] !== worldMapElement) {
    t41 = <Box onPointerDown={markMapNavigated} onWheel={markMapNavigated} sx={t39}>{worldMapElement}{t40}</Box>;
    $[114] = t40;
    $[115] = worldMapElement;
    $[116] = t41;
  } else {
    t41 = $[116];
  }
  const mapElement = t41;
  if (isScreenLgOrLarger) {
    let t42;
    if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
      t42 = {
        overflow: "hidden",
        height: "100%"
      };
      $[117] = t42;
    } else {
      t42 = $[117];
    }
    let t43;
    if ($[118] !== isMapFullscreen) {
      t43 = isMapFullscreen && {
        display: "none"
      };
      $[118] = isMapFullscreen;
      $[119] = t43;
    } else {
      t43 = $[119];
    }
    let t44;
    if ($[120] !== t43) {
      t44 = {
        width: "450px",
        flexShrink: 0,
        height: "100%",
        pr: 1,
        ...t43
      };
      $[120] = t43;
      $[121] = t44;
    } else {
      t44 = $[121];
    }
    let t45;
    if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
      t45 = {
        flex: 1,
        minHeight: 0
      };
      $[122] = t45;
    } else {
      t45 = $[122];
    }
    let t46;
    if ($[123] !== renderListContent) {
      t46 = <Box sx={t45}>{renderListContent()}</Box>;
      $[123] = renderListContent;
      $[124] = t46;
    } else {
      t46 = $[124];
    }
    let t47;
    if ($[125] !== t44 || $[126] !== t46 || $[127] !== toolbarElement) {
      t47 = <Stack sx={t44}>{toolbarElement}{t46}</Stack>;
      $[125] = t44;
      $[126] = t46;
      $[127] = toolbarElement;
      $[128] = t47;
    } else {
      t47 = $[128];
    }
    let t48;
    if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
      t48 = {
        flex: 1,
        minWidth: 0,
        height: "100%"
      };
      $[129] = t48;
    } else {
      t48 = $[129];
    }
    let t49;
    if ($[130] !== mapElement) {
      t49 = <Box key="projects-map" sx={t48}>{mapElement}</Box>;
      $[130] = mapElement;
      $[131] = t49;
    } else {
      t49 = $[131];
    }
    let t50;
    if ($[132] !== t47 || $[133] !== t49) {
      t50 = <ProjectsMapContainer><Stack direction="row" sx={t42}>{t47}{t49}</Stack></ProjectsMapContainer>;
      $[132] = t47;
      $[133] = t49;
      $[134] = t50;
    } else {
      t50 = $[134];
    }
    return t50;
  }
  let t42;
  if ($[135] !== isMapFullscreen) {
    t42 = isMapFullscreen && {
      display: "none"
    };
    $[135] = isMapFullscreen;
    $[136] = t42;
  } else {
    t42 = $[136];
  }
  let t43;
  if ($[137] !== t42) {
    t43 = {
      ...t42
    };
    $[137] = t42;
    $[138] = t43;
  } else {
    t43 = $[138];
  }
  let t44;
  if ($[139] !== t43 || $[140] !== toolbarElement) {
    t44 = <Box sx={t43}>{toolbarElement}</Box>;
    $[139] = t43;
    $[140] = toolbarElement;
    $[141] = t44;
  } else {
    t44 = $[141];
  }
  let t45;
  if ($[142] !== isMapFullscreen) {
    t45 = [isMapFullscreen];
    $[142] = isMapFullscreen;
    $[143] = t45;
  } else {
    t45 = $[143];
  }
  const t46 = isMapFullscreen ? 0 : 1;
  let t47;
  if ($[144] !== t46) {
    t47 = {
      height: "100%",
      gap: t46
    };
    $[144] = t46;
    $[145] = t47;
  } else {
    t47 = $[145];
  }
  const t48 = isMapFullscreen ? "100%" : "50%";
  let t49;
  if ($[146] !== t48) {
    t49 = {
      height: t48
    };
    $[146] = t48;
    $[147] = t49;
  } else {
    t49 = $[147];
  }
  let t50;
  if ($[148] !== mapElement || $[149] !== t49) {
    t50 = <Box key="projects-map" sx={t49}>{mapElement}</Box>;
    $[148] = mapElement;
    $[149] = t49;
    $[150] = t50;
  } else {
    t50 = $[150];
  }
  let t51;
  if ($[151] !== isMapFullscreen) {
    t51 = isMapFullscreen && {
      display: "none"
    };
    $[151] = isMapFullscreen;
    $[152] = t51;
  } else {
    t51 = $[152];
  }
  let t52;
  if ($[153] !== t51) {
    t52 = {
      height: "50%",
      ...t51
    };
    $[153] = t51;
    $[154] = t52;
  } else {
    t52 = $[154];
  }
  let t53;
  if ($[155] !== renderListContent) {
    t53 = renderListContent();
    $[155] = renderListContent;
    $[156] = t53;
  } else {
    t53 = $[156];
  }
  let t54;
  if ($[157] !== t52 || $[158] !== t53) {
    t54 = <Box sx={t52}>{t53}</Box>;
    $[157] = t52;
    $[158] = t53;
    $[159] = t54;
  } else {
    t54 = $[159];
  }
  let t55;
  if ($[160] !== t47 || $[161] !== t50 || $[162] !== t54) {
    t55 = <Stack sx={t47}>{t50}{t54}</Stack>;
    $[160] = t47;
    $[161] = t50;
    $[162] = t54;
    $[163] = t55;
  } else {
    t55 = $[163];
  }
  let t56;
  if ($[164] !== t45 || $[165] !== t55) {
    t56 = <ProjectsMapContainer heightDeps={t45}>{t55}</ProjectsMapContainer>;
    $[164] = t45;
    $[165] = t55;
    $[166] = t56;
  } else {
    t56 = $[166];
  }
  let t57;
  if ($[167] !== t44 || $[168] !== t56) {
    t57 = <>{t44}{t56}</>;
    $[167] = t44;
    $[168] = t56;
    $[169] = t57;
  } else {
    t57 = $[169];
  }
  return t57;
}
function _temp6(props) {
  return <XMarkCircleFillIcon {...props} sx={{
    ...props.sx,
    ...getSvgColoredIconCss(PALETTE_VARS.error.main),
    width: "35px",
    height: "35px"
  }} />;
}
function _temp5(project) {
  return <ProjectMarkerPopupContent project={project} />;
}
function _temp4(e) {
  return e.id;
}
function _temp3(p_3) {
  return p_3.id;
}
function _temp2(p_1) {
  return p_1.id;
}
function _temp(wasFullscreen) {
  return !wasFullscreen;
}